import {
  ICrewsNonFlyingAssignment,
  IDisplayBidNFA,
  IDisplayBidPairing,
  IDisplayPairing,
  IPairing,
} from "@vmosolutions/oc-interfaces";
import { API, Auth } from "aws-amplify";

import { IDynamicQuery, ISearchPairing } from "@vmosolutions/oc-interfaces";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { IHoveredEvent } from "src/pages/CrewSchedule/gl/CrewScheduleGL";
import { openSnackbar } from "../App.Actions";

export const getPairings = async (startTime: number, endTime: number): Promise<IDisplayPairing[]> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  const pairings = await API.get("skytrains", `/pairings?startTime=${startTime}&endTime=${endTime}`, {
    headers: {
      Accept: "*/*",
      Authorization: token,
    },
  });

  return pairings;
};

/**
 * Modified find function applied only to bid pairings for the time beingf to test fix.
 */
export const findPairingsFromTime2 = (
  pairings: (IDisplayPairing | IDisplayBidPairing)[],
  time: number,
  startIndex: number,
  endIndex: number,
  // foundPairings: IDisplayPairing[] = [], - removed as it wasn't even being used by this function.
  checkNeighbors: boolean
): [IDisplayPairing | IDisplayBidPairing | null, number] => {
  if (startIndex > endIndex) {
    return [null, -1];
  }
  const mid = Math.floor((endIndex + startIndex) / 2);
  const pairing = pairings[mid];
  if (time >= pairing.start?.valueOf() && time <= pairing.end?.valueOf()) {
    return [pairing, mid];
  }

  // The following 2 conditions have been added in to resolve an issue where some some bid pairings are not being found on hover.
  //TODO investigate better way to improve base function.
  if (checkNeighbors && time >= pairings[mid - 1]?.start?.valueOf() && time <= pairings[mid - 1]?.end?.valueOf()) {
    // CHECK NEXT
    return [pairings[mid - 1], mid - 1];
  } else if (
    checkNeighbors &&
    time >= pairings[mid + 1]?.start.valueOf() &&
    time <= pairings[mid + 1]?.end?.valueOf()
  ) {
    // CHECK PREV
    return [pairings[mid + 1], mid + 1];
  }

  if (pairing.end?.valueOf() > time) {
    // ensure no overlap on next item
    if (mid + 1 < pairings.length) {
      const nextPairing = pairings[mid + 1];
      if (time >= nextPairing.start?.valueOf() && time <= nextPairing.end?.valueOf()) {
        return [nextPairing, mid + 1];
      }
    }
    return findPairingsFromTime2(pairings, time, startIndex, mid - 1, checkNeighbors);
  } else {
    // ensure no overlap on prev item
    if (mid - 1 >= 0) {
      const prevItem = pairings[mid - 1];
      if (time >= prevItem.start?.valueOf() && time <= prevItem.end?.valueOf()) {
        return [prevItem, mid - 1];
      }
    }
    return findPairingsFromTime2(pairings, time, mid + 1, endIndex, checkNeighbors);
  }
};

export const findPairingsFromTime = (
  pairings: IDisplayPairing[],
  time: number,
  startIndex: number,
  endIndex: number,
  foundPairings: IDisplayPairing[] = []
): [IDisplayPairing | null, number] => {
  if (startIndex > endIndex) {
    return [null, -1];
  }
  const mid = Math.floor((endIndex + startIndex) / 2);
  const pairing = pairings[mid];
  if (time >= pairing.start?.valueOf() && time <= pairing.end?.valueOf()) {
    return [pairing, mid];
  }

  if (pairing.end.valueOf() > time) {
    // ensure no overlap on next item
    if (mid + 1 < pairings.length) {
      const nextPairing = pairings[mid + 1];
      if (time >= nextPairing.start?.valueOf() && time <= nextPairing.end?.valueOf()) {
        return [nextPairing, mid + 1];
      }
    }
    return findPairingsFromTime(pairings, time, startIndex, mid - 1, foundPairings);
  } else {
    // ensure no overlap on prev item
    if (mid - 1 >= 0) {
      const prevItem = pairings[mid - 1];
      if (time >= prevItem.start?.valueOf() && time <= prevItem.end?.valueOf()) {
        return [prevItem, mid - 1];
      }
    }

    return findPairingsFromTime(pairings, time, mid + 1, endIndex, foundPairings);
  }
};

export const getOverlappingEvents = (
  hoveredEvent: IDisplayPairing | IDisplayBidPairing | ICrewsNonFlyingAssignment | IDisplayBidNFA,
  eventIndex: number,
  events: (IDisplayPairing | IDisplayBidPairing | ICrewsNonFlyingAssignment | IDisplayBidNFA)[],
  crewId: string,
  isPlanned: boolean
): IHoveredEvent[] => {
  const overlappingEvents: IHoveredEvent[] = [];
  if (eventIndex >= 0 && eventIndex < events.length) {
    for (let i = eventIndex - 1; i >= 0; i--) {
      if (events[i].start <= hoveredEvent.end && hoveredEvent.start <= events[i].end) {
        overlappingEvents.push({ event: events[i], crewId: crewId, isPlanned });
      } else {
        break;
      }
    }
    for (let i = eventIndex + 1; i < events.length; i++) {
      if (events[i].start <= hoveredEvent.end && hoveredEvent.start <= events[i].end) {
        overlappingEvents.push({ event: events[i], crewId: crewId, isPlanned });
      } else {
        break;
      }
    }
  }
  return overlappingEvents;
};

export const findCrewEventFromTime = (
  crewEvents: (IDisplayPairing | ICrewsNonFlyingAssignment | IDisplayBidNFA)[],
  time: number,
  startIndex: number,
  endIndex: number
): [IDisplayPairing | ICrewsNonFlyingAssignment | IDisplayBidNFA | null, number] => {
  if (startIndex > endIndex) {
    return [null, -1];
  }
  const mid = Math.floor((endIndex + startIndex) / 2);
  const crewEvent = crewEvents[mid];
  if (time >= crewEvent?.start?.valueOf() && time <= crewEvent?.end?.valueOf()) {
    return [crewEvent, mid];
  }

  if (crewEvent.end?.valueOf() > time) {
    return findCrewEventFromTime(crewEvents, time, startIndex, mid - 1);
  } else {
    return findCrewEventFromTime(crewEvents, time, mid + 1, endIndex);
  }
};

// export const getAvailableCrewPairings = async (crewId: string, time: number): Promise<IDisplayPairing[]> => {
//   const userSession = await Auth.currentSession();
//   const token = userSession.getIdToken().getJwtToken();

//   try {
//     const pairings = await API.get("skytrains", `/availablePairings/${crewId}?time=${time}`, {
//       headers: {
//         Accept: "*/*",
//         Authorization: token,
//       },
//     });

//     return pairings;
//   } catch (error) {
//     // console.error(error);
//     return [];
//   }
// };

export const removePairingFromCrew = async (pairingId: string, crewMemberId: string): Promise<void> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  try {
    const response: IDisplayPairing = await API.put(
      "skytrains",
      `/removeCrewMemberFromPairing/${pairingId}/crewId/${crewMemberId}`,
      {
        headers: {
          Accept: "*/*",
          Authorization: token,
        },
      }
    );

    // @ts-ignore
    return response;
  } catch (error) {
    // console.error(error);
    return null;
  }
};

export const assignPairingToCrew = async (crewId: string, pairingId: string): Promise<any> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  try {
    const response: IDisplayPairing = await API.put("skytrains", `/assignCrewToPairing/${pairingId}/crewId/${crewId}`, {
      headers: {
        Accept: "*/*",
        Authorization: token,
      },
    });

    // @ts-ignore
    return response;
  } catch (error) {
    // console.error(error);
    return null;
  }
};

export const splitSelectedPairing = async (
  splitPayload: { pairingId: string; crewIds: string[] },
  dispatch: ThunkDispatch<object, object, AnyAction>
): Promise<IPairing> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  dispatch(
    openSnackbar({
      open: true,
      closeDelay: null,
      variant: "info",
      message: "Splitting Selected Pairing...",
    })
  );

  //delete pairingData.eventGroups.ren
  const response = await API.post("skytrains", "/pairings/split", {
    headers: {
      Accept: "*/*",
      Authorization: token,
    },
    body: splitPayload,
  });

  if (response?._id) {
    // SUCCESS
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "success",
        message: "Pairing Successfully Split",
      })
    );
  } else {
    // FAILURE
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "error",
        message: "Something Went Wrong!",
      })
    );
  }
  return response;
};

export const deletePairing = async (
  pairingId: string,
  dispatch: ThunkDispatch<object, object, AnyAction>
): Promise<any> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  dispatch(
    openSnackbar({
      open: true,
      closeDelay: null,
      variant: "info",
      message: "Deleting Selected Pairing...",
    })
  );

  const response = await API.del("skytrains", `/pairings/${pairingId}`, {
    headers: {
      Accept: "*/*",
      Authorization: token,
    },
  });

  if (response?.deleted) {
    // SUCCESS
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "success",
        message: response?.message, //"Pairing Successfully Deleted",
      })
    );
  } else {
    // FAILURE
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "error",
        message: "Something Went Wrong!",
      })
    );
  }
  return response;
};

export const deletePlannedPairing = async (
  pairingId: string,
  dispatch: ThunkDispatch<object, object, AnyAction>
): Promise<any> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  dispatch(
    openSnackbar({
      open: true,
      closeDelay: null,
      variant: "info",
      message: "Deleting Planned Pairing...",
    })
  );

  const response = await API.del("skytrains", `/pairings/planned/${pairingId}`, {
    headers: {
      Accept: "*/*",
      Authorization: token,
    },
  });

  if (response?.deleted) {
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "success",
        message: response?.message ?? "Planned pairing successfully deleted.",
      })
    );
  } else {
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "error",
        message: "Something Went Wrong!",
      })
    );
  }

  return response;
};

// SEARCH PAIRINGS
export const searchPairings = async (
  payload: IDynamicQuery,
  dispatch: ThunkDispatch<object, object, AnyAction>
): Promise<ISearchPairing | any> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  try {
    const response = await API.post("skytrains", "/pairings/search", {
      headers: {
        Accept: "*/*",
        Authorization: token,
      },
      body: payload,
    });

    // NOTE: Search results are no longer injected in the redux dataset.
    // Loading takes place when the users attempt to focus on a selected search result to view it in the crew sched.

    // Append pairings found to the redux pairing Data.
    // if (Array.isArray(response) && response.length > 0) {
    //  dispatch(gotPairings(response, true));
    // }

    return response;
  } catch (error) {
    //@ts-ignore
    return error?.response;
  }
};
